import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as exportCatalogApi from '../api/exportCatalog';
import * as exportJobsApi from '../api/exportJobs';
import * as deliveryConfigsApi from '../api/deliveryConfigs';

export function ExportsModule() {
  const { selectedOrg } = useOrg();
  const { canReadAssets } = usePermissions();
  const [datasets, setDatasets] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('datasets');
  const [exporting, setExporting] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState(null);

  const loadData = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [catalogData, jobsData, logsData] = await Promise.all([
        exportCatalogApi.getExportCatalog(selectedOrg.id),
        exportJobsApi.getExportJobs(selectedOrg.id, { limit: 20 }),
        deliveryConfigsApi.getDeliveryLogs(selectedOrg.id, { limit: 20 })
      ]);
      setDatasets(catalogData);
      setJobs(jobsData);
      setLogs(logsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async (dataset) => {
    if (!selectedOrg || exporting) return;
    setExporting(dataset.id);
    try {
      await exportJobsApi.createExportJob(selectedOrg.id, dataset.type, {});
      alert(`Export started for ${dataset.name}`);
      loadData();
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setExporting(null);
    }
  };

  const handleOpenDetails = (dataset) => {
    setSelectedDataset(dataset);
    setShowModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': case 'success': return { bg: '#c6f6d5', color: '#276749' };
      case 'running': case 'pending': return { bg: '#fefcbf', color: '#975a16' };
      case 'failed': return { bg: '#fed7d7', color: '#c53030' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  const handleDownload = async (artifactId) => {
    if (!selectedOrg || !artifactId) return;
    try {
      const result = await exportJobsApi.downloadExportArtifact(selectedOrg.id, artifactId);
      alert(`Download ready: ${result.filename}`);
    } catch (err) {
      alert('Download failed: ' + err.message);
    }
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to access exports.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Data Exports"
        subtitle="Export datasets and view export history"
      />

      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab('datasets')}
          style={activeTab === 'datasets' ? styles.activeTab : styles.tab}
          data-testid="datasets-tab"
        >
          Datasets
        </button>
        <button
          onClick={() => setActiveTab('history')}
          style={activeTab === 'history' ? styles.activeTab : styles.tab}
          data-testid="history-tab"
        >
          Export History
        </button>
        <button
          onClick={() => setActiveTab('delivery')}
          style={activeTab === 'delivery' ? styles.activeTab : styles.tab}
          data-testid="delivery-tab"
        >
          Delivery Logs
        </button>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading...</div>
      ) : activeTab === 'datasets' ? (
        <div style={styles.datasetsGrid} data-testid="datasets-grid">
          {datasets.map(dataset => (
            <div key={dataset.id} style={styles.datasetCard} data-testid={`dataset-${dataset.id}`}>
              <h3 style={styles.datasetName}>{dataset.name}</h3>
              <p style={styles.datasetDesc}>{dataset.description}</p>
              <div style={styles.datasetMeta}>
                <span style={styles.fieldCount}>{dataset.fields.length} fields</span>
                <span style={styles.formatBadge}>{dataset.formats.join(', ')}</span>
              </div>
              <div style={styles.datasetActions}>
                <button
                  onClick={() => handleOpenDetails(dataset)}
                  style={styles.detailsButton}
                  data-testid={`details-${dataset.id}`}
                >
                  View Fields
                </button>
                <button
                  onClick={() => handleExport(dataset)}
                  disabled={exporting === dataset.id}
                  style={styles.exportButton}
                  data-testid={`export-${dataset.id}`}
                >
                  {exporting === dataset.id ? 'Exporting...' : 'Export'}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : activeTab === 'history' ? (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="history-table">
            <thead>
              <tr>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Progress</th>
                <th style={styles.th}>Started</th>
                <th style={styles.th}>Completed</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => {
                const statusStyle = getStatusColor(job.status);
                return (
                  <tr key={job.id} style={styles.tr}>
                    <td style={styles.td}><strong>{job.type}</strong></td>
                    <td style={styles.td}>
                      <span style={{ ...styles.statusBadge, background: statusStyle.bg, color: statusStyle.color }}>
                        {job.status}
                      </span>
                    </td>
                    <td style={styles.td}>{job.progress}%</td>
                    <td style={styles.td}>{new Date(job.createdAt).toLocaleString()}</td>
                    <td style={styles.td}>{job.completedAt ? new Date(job.completedAt).toLocaleString() : '-'}</td>
                    <td style={styles.td}>
                      {job.status === 'completed' && job.artifactId && (
                        <button
                          onClick={() => handleDownload(job.artifactId)}
                          style={styles.downloadButton}
                        >
                          Download
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {jobs.length === 0 && (
                <tr>
                  <td colSpan="6" style={styles.emptyState}>No export history yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="delivery-table">
            <thead>
              <tr>
                <th style={styles.th}>Time</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Filename</th>
                <th style={styles.th}>Remote Host</th>
                <th style={styles.th}>Bytes</th>
                <th style={styles.th}>Error</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const statusStyle = getStatusColor(log.status);
                return (
                  <tr key={log.id} style={styles.tr}>
                    <td style={styles.td}>{new Date(log.startedAt).toLocaleString()}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.statusBadge, background: statusStyle.bg, color: statusStyle.color }}>
                        {log.status}
                      </span>
                    </td>
                    <td style={styles.td}>{log.filename}</td>
                    <td style={styles.td}>{log.remoteHost}</td>
                    <td style={styles.td}>{log.bytesTransferred}</td>
                    <td style={styles.td}>{log.error || '-'}</td>
                  </tr>
                );
              })}
              {logs.length === 0 && (
                <tr>
                  <td colSpan="6" style={styles.emptyState}>No delivery logs yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && selectedDataset && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="dataset-modal">
            <h3 style={styles.modalTitle}>{selectedDataset.name}</h3>
            <p style={styles.modalDesc}>{selectedDataset.description}</p>
            
            <h4 style={styles.sectionTitle}>Available Fields</h4>
            <div style={styles.fieldsList}>
              {selectedDataset.fields.map(field => (
                <span key={field} style={styles.fieldTag}>{field}</span>
              ))}
            </div>
            
            <h4 style={styles.sectionTitle}>Filters</h4>
            <div style={styles.fieldsList}>
              {selectedDataset.filters.map(filter => (
                <span key={filter} style={styles.filterTag}>{filter}</span>
              ))}
            </div>
            
            <h4 style={styles.sectionTitle}>Formats</h4>
            <div style={styles.fieldsList}>
              {selectedDataset.formats.map(format => (
                <span key={format} style={styles.formatTag}>{format.toUpperCase()}</span>
              ))}
            </div>
            
            <div style={styles.modalActions}>
              <button onClick={() => setShowModal(false)} style={styles.cancelButton}>
                Close
              </button>
              <button
                onClick={() => { handleExport(selectedDataset); setShowModal(false); }}
                style={styles.exportButton}
              >
                Export Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  tabs: { display: 'flex', gap: '0.5rem', marginBottom: '1rem' },
  tab: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  activeTab: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #3182ce', background: '#ebf8ff', color: '#3182ce', fontSize: '0.875rem', cursor: 'pointer' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  datasetsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' },
  datasetCard: { background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column' },
  datasetName: { margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600', color: '#2d3748' },
  datasetDesc: { margin: '0 0 1rem 0', fontSize: '0.875rem', color: '#718096', flex: 1 },
  datasetMeta: { display: 'flex', gap: '0.5rem', marginBottom: '1rem' },
  fieldCount: { fontSize: '0.75rem', color: '#4a5568', background: '#f7fafc', padding: '0.25rem 0.5rem', borderRadius: '4px' },
  formatBadge: { fontSize: '0.75rem', color: '#2b6cb0', background: '#ebf8ff', padding: '0.25rem 0.5rem', borderRadius: '4px', textTransform: 'uppercase' },
  datasetActions: { display: 'flex', gap: '0.5rem' },
  detailsButton: { flex: 1, padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  exportButton: { flex: 1, padding: '0.5rem', borderRadius: '6px', border: '1px solid #3182ce', background: '#3182ce', color: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.75rem', color: '#4a5568', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem' },
  statusBadge: { padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', textTransform: 'capitalize' },
  downloadButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #3182ce', background: 'white', color: '#3182ce', fontSize: '0.75rem', cursor: 'pointer' },
  emptyState: { padding: '2rem', textAlign: 'center', color: '#718096' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: '8px', padding: '1.5rem', minWidth: '500px', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' },
  modalTitle: { margin: '0 0 0.5rem 0', fontSize: '1.25rem', fontWeight: '600' },
  modalDesc: { margin: '0 0 1.5rem 0', fontSize: '0.875rem', color: '#718096' },
  sectionTitle: { margin: '0 0 0.5rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#4a5568' },
  fieldsList: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' },
  fieldTag: { padding: '0.25rem 0.5rem', background: '#f7fafc', borderRadius: '4px', fontSize: '0.75rem', color: '#4a5568' },
  filterTag: { padding: '0.25rem 0.5rem', background: '#fefcbf', borderRadius: '4px', fontSize: '0.75rem', color: '#975a16' },
  formatTag: { padding: '0.25rem 0.5rem', background: '#c6f6d5', borderRadius: '4px', fontSize: '0.75rem', color: '#276749' },
  modalActions: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' },
  cancelButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', fontSize: '0.875rem', cursor: 'pointer' }
};
